import { hexToRgb } from "common/utilities/colors/colors";
import { arrayFlat } from "common/utilities/core/array";
import type {
  EntitySequence as Sequence,
  SequenceAlign,
  SequenceFeature,
  SequenceFeatureColor,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import * as defaultsColors from "sequenceAnalyser/utilities/featuresColors/defaults";
import {
  generateFeaturesColors,
  getFeatureTypeColorSetting,
} from "sequenceAnalyser/utilities/featuresColors/featuresColors";
import {
  ColorSources,
  type FeatureDefinition,
  type FeatureDefinitions,
  type FeatureId,
  type FeatureTypesColor,
  type Residue,
  type SequenceFeaturesType,
  type SequenceFeatureWithRef,
  type VisibleSequencesFeatures,
} from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { buildFeatureId, residuesStringToArray } from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

type Positions = Record<number, number>;

/**
 * Within an aligned sequences, calculate residues positions from their initial sequence.
 * Example: ..ABC. => { 1: 2, 2: 3, 3: 4 }
 */
export function residuesPositionsToAlignedPositions(residues: string): Positions {
  const arr = residuesStringToArray(residues);

  let gapCount = 0;
  let lastResiduePosition = 0;

  const positions: Positions = arr.reduce<Positions>((acc, residue) => {
    if (residue === ".") {
      gapCount += 1;
    } else {
      lastResiduePosition += 1;
      acc[lastResiduePosition] = gapCount + lastResiduePosition - 1;
    }

    return acc;
  }, {});

  return positions;
}

/**
 * We transform a feature positions that don't take gaps into consideration to aligned positions.
 * Example: ..ABC. position 1 to 3 is AB, after format it will be 2 to 4
 * Note: initial position starts at 1. Formatted position starts at 0 (standard array).
 */
export function formatSequencesFeaturesPositionsToAlignedPositions(
  sequences: Sequence[],
  alignedSequences: SequenceAlign[],
): Sequence[] {
  const newSequences = sequences.map((s) => {
    const alignedSequence = alignedSequences.find((as) => as.id === s.id);
    if (!alignedSequence) return s;

    const newPositions = residuesPositionsToAlignedPositions(alignedSequence.residues);

    return {
      ...s,
      features: s.features.map((f) => ({
        ...f,
        start: newPositions[f.start],
        stop: newPositions[f.stop],
      })),
    };
  });

  return newSequences;
}

export function sortSequencesFeaturesByLength(sequences: Sequence[]): Sequence[] {
  return sequences.map((s) => ({
    ...s,
    features: [...s.features].sort((f1, f2) => f2.stop - f2.start - (f1.stop - f1.start)),
  }));
}

export function createFeatureDefinitions(
  sequences: Sequence[],
  userColors: SequenceFeatureColor[],
  existingFeatureDefinitions: FeatureDefinitions,
  existingFeatureTypesColor: FeatureTypesColor,
) {
  const features = arrayFlat<SequenceFeatureWithRef>(
    sequences.map((s) => s.features.map((f) => ({ ...f, sequenceName: s.name }))),
  );

  /** Build colors for feature types and features */
  const featuresColors = generateFeaturesColors(features, existingFeatureTypesColor, userColors, defaultsColors);
  const updatedFeatureTypesColors: FeatureTypesColor = featuresColors.featureTypesColor;

  const featureDefinitions = features.reduce<FeatureDefinitions>((acc, feature) => {
    const featureId: FeatureId = buildFeatureId(feature);

    if (acc[featureId]) {
      acc[featureId].features.push(feature);
    } else {
      // Ensure we keep previous visibility setting
      const visible = existingFeatureDefinitions[featureId]?.visible;

      const forceColor = feature.forceColor ? hexToRgb(feature.forceColor) : undefined;

      acc[featureId] = {
        // This is the name displayed in the UI per track or in the table
        name: feature.name ?? feature.type,
        uniqueTypeString: featureId,
        typeId: feature.id,
        type: feature.type,
        color: forceColor || featuresColors.featuresColors[featureId].color, // update feature color
        source: forceColor ? ColorSources.forced : featuresColors.featuresColors[featureId].source,
        visible: visible === undefined ? true : visible,
        features: [feature],
      };
      // update feature type color (category in feature table)
      updatedFeatureTypesColors[feature.type].color = forceColor || featuresColors.featuresColors[featureId].color;
    }
    return acc;
  }, {});

  return {
    featureDefinitions,
    featuresColors: featuresColors.featuresColors,
    featureTypesColor: updatedFeatureTypesColors,
  };
}

export function areAllFeaturesDefinitionsHidden(definitions: FeatureDefinition[]) {
  return !definitions.some((def) => def.visible);
}

export function groupFeaturesDefinitionByType(
  definitions: FeatureDefinitions,
  userColors: SequenceFeatureColor[],
  featureTypesColor: FeatureTypesColor,
): SequenceFeaturesType[] {
  return Object.values(definitions).reduce<SequenceFeaturesType[]>((types, fd: FeatureDefinition) => {
    const type = types.find((t) => t.type === fd.type);
    if (type) {
      type.definitions.push(fd);
    } else {
      const userColor = userColors.find((c) => c.featureTypeId === fd.typeId && !c.label);
      const newType: SequenceFeaturesType = {
        uniqueTypeString: fd.uniqueTypeString,
        typeId: fd.typeId,
        type: fd.type,
        definitions: [fd],
        colorSetting: getFeatureTypeColorSetting(fd.type, userColor, defaultsColors.defaultFeatureTypesColorSetting),
        color: featureTypesColor[fd.type].color,
        colorSource: featureTypesColor[fd.type].source,
      };
      types.push(newType);
    }
    return types;
  }, []);
}

export function featuresForRegion(features: SequenceFeature[], region: number) {
  return features.filter((feature) => feature.start <= region && feature.stop >= region);
}

export function featureDefinitionsForRegion(
  features: SequenceFeature[],
  featureDefinitions: FeatureDefinitions,
  region: number,
) {
  const matchingFeatures = featuresForRegion(features, region);
  return matchingFeatures.map((feature) => featureDefinitions[buildFeatureId(feature)]);
}

export function featuresForResidue(sequences: Sequence[], residue: Residue) {
  const sequence = sequences.find((s) => s.qualifiedId === residue.sequenceQualifiedId);
  return featuresForRegion(sequence ? sequence.features : [], residue.position);
}

export function groupFeaturesByName(features: SequenceFeature[]) {
  return features.reduce<Record<string, SequenceFeature[]>>((groups, feature) => {
    const newGroups = { ...groups };
    newGroups[buildFeatureId(feature)] = [...(newGroups[buildFeatureId(feature)] || []), feature];
    return newGroups;
  }, {});
}

export const getVisibleSequencesFeatures = (sequences: Sequence[], featureDefinitions: FeatureDefinitions) =>
  sequences.reduce<VisibleSequencesFeatures>((acc, sequence) => {
    const newAcc: VisibleSequencesFeatures = { ...acc };
    newAcc[sequence.id] = sequence.features.filter((feature) => featureDefinitions[buildFeatureId(feature)].visible);
    return newAcc;
  }, {});
